import { Checkbox, Flex, Td, Tr } from '@chakra-ui/react'
import { formatHeightValue } from '@hb/react'
import {
  BirthingCenterAdmissionData,
  Encounter,
  EncounterExamData,
  EncounterStatus,
  EncounterType,
  PostpartumExamData,
  UltrasoundData,
  VitalSigns,
  WellWomanExamData,
  formatDropdownValue,
  fundalHeightLandmarkField,
  getDateString,
} from '@hb/shared'
import { useMemo } from 'react'
import { EncounterSelection } from '../../types'
import { useEncounterStatusLabel } from '../../useEncounterStatusLabel'

// Status cell — routes through the app-split label helper so the admin app reads
// "Saved — Ready for Auth" for COMPLETED while the providers app keeps "Completed".
const EncounterStatusCell = ({ status }: { status: EncounterStatus }) => (
  <Td>{useEncounterStatusLabel(status)}</Td>
)

export interface EncounterRowProps<T extends EncounterType = EncounterType> {
  encounter: Encounter<T>
  encounterId: string
  onOpenEncounter: (exam: EncounterSelection<T>) => void
  isSelecting: boolean
  isChecked?: boolean
  onToggle: () => void
}

const getBmi = (heightIn?: number, weightLbs?: number) => {
  if (!heightIn || !weightLbs) return null
  return ((weightLbs * 703) / (heightIn * heightIn)).toFixed(1)
}

const formatBp = (bp?: { systolic: number; diastolic: number }) => {
  if (!bp) return '-'
  return `${bp.systolic}/${bp.diastolic}`
}

const formatGa = (totalDays?: number) => {
  if (totalDays == null) return '-'
  const weeks = Math.floor(totalDays / 7)
  const days = totalDays % 7
  return `${weeks}w ${days}d`
}

const formatTime = (ms?: number) => {
  if (!ms) return '-'
  return new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

const admissionTypeLabels: Record<BirthingCenterAdmissionData['admissionType'], string> = {
  'spontaneous-labor': 'Spontaneous Labor',
  prom: 'PROM',
  elective: 'Elective',
  induction: 'Induction',
  'transfer-in': 'Transfer In',
}

const membraneStatusLabels: Record<BirthingCenterAdmissionData['membraneStatus'], string> = {
  intact: 'Intact',
  srom: 'SROM',
  arom: 'AROM',
}

const fetalPositionLabels: Record<string, string> = {
  cephalic: 'Cephalic',
  'breech-frank': 'Breech — Frank',
  'breech-complete': 'Breech — Complete',
  'breech-footling': 'Breech — Footling',
  transverse: 'Transverse',
  oblique: 'Oblique',
  'not-yet-clinically-relevant': 'Not Yet Relevant',
  // legacy
  breech: 'Breech',
  other: 'Other',
}

const birthPlanLabels: Record<string, string> = {
  yes: 'Yes',
  no: 'No',
  'in-progress': 'In Progress',
}

const useInitials = (exam: Encounter) =>
  useMemo(
    () =>
      Array.from(
        new Set(
          [
            exam.renderingProviderInitials,
            exam.supervisingProviderInitials,
            exam.attendingProviderInitials,
          ].filter(Boolean),
        ),
      ).join(', ') || '-',
    [
      exam.renderingProviderInitials,
      exam.supervisingProviderInitials,
      exam.attendingProviderInitials,
    ],
  )

const getRowClickHandler =
  <T extends EncounterType>({
    isSelecting,
    onToggle,
    onOpenEncounter: onOpenExam,
    encounter: exam,
    encounterId: examId,
  }: EncounterRowProps<T>) =>
  () =>
    isSelecting
      ? onToggle()
      : onOpenExam({ type: exam.type, examId, config: exam.config, status: exam.status })

const SelectTd = ({
  isSelecting,
  isChecked,
  onToggle,
}: Pick<EncounterRowProps, 'isSelecting' | 'isChecked' | 'onToggle'>) =>
  isSelecting ? (
    <Td
      w="38px"
      px={0}
      borderLeft="1px solid"
      borderColor="gray.100"
      onClick={e => {
        e.stopPropagation()
        onToggle()
      }}>
      <Flex justify="center">
        <Checkbox colorScheme="green" isChecked={isChecked} pointerEvents="none" />
      </Flex>
    </Td>
  ) : null

const PhysicalExamRow: React.FC<EncounterRowProps<EncounterType.PHYSICAL>> = props => {
  const { encounter: exam, isSelecting, isChecked, onToggle } = props
  const { data } = exam ?? {}
  const vital = data?.vital
  const initials = useInitials(exam)
  return (
    <Tr cursor="pointer" h="40px" _hover={{ bg: 'gray.50' }} onClick={getRowClickHandler(props)}>
      <Td>{exam.date ? getDateString(exam.date, 'short', true) : '-'}</Td>
      <Td>{vital?.height ? formatHeightValue(vital.height) : '-'}</Td>
      <Td>{vital?.weight ? `${vital.weight} lbs` : '-'}</Td>
      <Td>{getBmi(vital?.height, vital?.weight) ?? '-'}</Td>
      <Td>{formatBp(vital?.bloodPressure)}</Td>
      <Td>{vital?.pulse ?? '-'}</Td>
      <Td>{vital?.temperature ? `${vital.temperature}°F` : '-'}</Td>
      <Td>{initials}</Td>
      <SelectTd isSelecting={isSelecting} isChecked={isChecked} onToggle={onToggle} />
    </Tr>
  )
}

const EncounterExamRow: React.FC<EncounterRowProps<EncounterType.ENCOUNTER>> = props => {
  const { encounter: exam, isSelecting, isChecked, onToggle } = props
  const { data } = exam ?? {}
  const d = data as EncounterExamData & { vital?: VitalSigns }
  const vital = d.vital
  const initials = useInitials(exam)
  return (
    <Tr cursor="pointer" h="40px" _hover={{ bg: 'gray.50' }} onClick={getRowClickHandler(props)}>
      <Td>{exam.date ? getDateString(exam.date, 'short', true) : '-'}</Td>
      <Td>{vital?.weight ? `${vital.weight} lbs` : '-'}</Td>
      <Td>{formatBp(vital?.bloodPressure)}</Td>
      <Td>{d.chiefComplaint || '-'}</Td>
      <Td>{d.hasPhysicalExam ? 'Yes' : '-'}</Td>
      <Td>{d.hasPelvicExam ? 'Yes' : '-'}</Td>
      <Td>{initials}</Td>
      <SelectTd isSelecting={isSelecting} isChecked={isChecked} onToggle={onToggle} />
    </Tr>
  )
}

const WellWomanExamRow: React.FC<EncounterRowProps<EncounterType.WELL_WOMAN>> = props => {
  const { encounter: exam, isSelecting, isChecked, onToggle } = props
  const { data } = exam ?? {}
  const d = (data ?? {}) as WellWomanExamData & { vital?: VitalSigns }
  const vital = d.vital
  const initials = useInitials(exam)
  return (
    <Tr cursor="pointer" h="40px" _hover={{ bg: 'gray.50' }} onClick={getRowClickHandler(props)}>
      <Td>{exam.date ? getDateString(exam.date, 'short', true) : '-'}</Td>
      <Td>{vital?.weight ? `${vital.weight} lbs` : '-'}</Td>
      <Td>{formatBp(vital?.bloodPressure)}</Td>
      <Td>{d.chiefComplaint || '-'}</Td>
      <Td>{d.hasPhysicalExam ? 'Yes' : '-'}</Td>
      <Td>{d.hasEducation ? 'Yes' : '-'}</Td>
      <Td>{initials}</Td>
      <SelectTd isSelecting={isSelecting} isChecked={isChecked} onToggle={onToggle} />
    </Tr>
  )
}

const AntepartumEncounterRow: React.FC<EncounterRowProps<EncounterType.ANTEPARTUM>> = props => {
  const { encounter: exam, isSelecting, isChecked, onToggle } = props
  const { data = {} } = exam ?? {}
  const vital = data.vital
  const initials = useInitials(exam)
  return (
    <Tr cursor="pointer" h="40px" _hover={{ bg: 'gray.50' }} onClick={getRowClickHandler(props)}>
      <Td>{exam.date ? getDateString(exam.date, 'short', true) : '-'}</Td>
      <Td>{formatGa(data?.gestationalAge)}</Td>
      <Td>{vital?.weight ? `${vital.weight} lbs` : '-'}</Td>
      <Td>{formatBp(vital?.bloodPressure)}</Td>
      <Td>{vital?.pulse ?? '-'}</Td>
      <Td>{vital?.temperature ? `${vital.temperature}°F` : '-'}</Td>
      <Td>
        {(data.fundalHeightCm ?? data.fundalHeight)
          ? `${data.fundalHeightCm ?? data.fundalHeight} cm`
          : data.fundalHeightLandmark
            ? formatDropdownValue(data.fundalHeightLandmark, fundalHeightLandmarkField.options)
            : '-'}
      </Td>
      <Td>{data.fetalHeartTones ? `${data.fetalHeartTones} bpm` : '-'}</Td>
      <Td>
        {data.fetalPosition ? (fetalPositionLabels[data.fetalPosition] ?? data.fetalPosition) : '-'}
      </Td>
      <Td>{initials}</Td>
      <EncounterStatusCell status={exam.status} />
      <SelectTd isSelecting={isSelecting} isChecked={isChecked} onToggle={onToggle} />
    </Tr>
  )
}

const PostpartumEncounterRow: React.FC<EncounterRowProps<EncounterType.POSTPARTUM>> = props => {
  const { encounter: exam, isSelecting, isChecked, onToggle } = props
  const d = exam.data as PostpartumExamData & { vital?: VitalSigns }
  const vital = d.vital
  const initials = useInitials(exam)
  const lochiaLabel = [d.lochia?.type, d.lochia?.amount].filter(Boolean).join(' / ') || '-'
  return (
    <Tr cursor="pointer" h="40px" _hover={{ bg: 'gray.50' }} onClick={getRowClickHandler(props)}>
      <Td>{exam.date ? getDateString(exam.date, 'short', true) : '-'}</Td>
      <Td>{formatBp(vital?.bloodPressure)}</Td>
      <Td>{vital?.pulse ?? '-'}</Td>
      <Td>{vital?.temperature ? `${vital.temperature}°F` : '-'}</Td>
      <Td>{lochiaLabel}</Td>
      <Td>{d.fundus || '-'}</Td>
      <Td>{d.perineum?.value ?? '-'}</Td>
      <Td>{initials}</Td>
      <EncounterStatusCell status={exam.status} />
      <SelectTd isSelecting={isSelecting} isChecked={isChecked} onToggle={onToggle} />
    </Tr>
  )
}

const BirthingCenterAdmissionRow: React.FC<
  EncounterRowProps<EncounterType.BIRTHING_CENTER_ADMISSION>
> = props => {
  const { encounter: exam, isSelecting, isChecked, onToggle } = props
  const d = exam.data as BirthingCenterAdmissionData
  const initials = useInitials(exam)
  return (
    <Tr cursor="pointer" h="40px" _hover={{ bg: 'gray.50' }} onClick={getRowClickHandler(props)}>
      <Td>{exam.date ? getDateString(exam.date, 'short', true) : '-'}</Td>
      <Td>{formatTime(d.admittedAt)}</Td>
      <Td>{d.admissionType ? (admissionTypeLabels[d.admissionType] ?? d.admissionType) : '-'}</Td>
      <Td>
        {d.membraneStatus ? (membraneStatusLabels[d.membraneStatus] ?? d.membraneStatus) : '-'}
      </Td>
      <Td>{d.membraneFluidColor ?? '-'}</Td>
      <Td>{d.birthPlanOnFile ? (birthPlanLabels[d.birthPlanOnFile] ?? d.birthPlanOnFile) : '-'}</Td>
      <Td>{initials}</Td>
      <SelectTd isSelecting={isSelecting} isChecked={isChecked} onToggle={onToggle} />
    </Tr>
  )
}

const UltrasoundRow: React.FC<EncounterRowProps<EncounterType.ULTRASOUND>> = props => {
  const { encounter: exam, isSelecting, isChecked, onToggle } = props
  const d = exam.data as UltrasoundData
  const initials = useInitials(exam)
  return (
    <Tr cursor="pointer" h="40px" _hover={{ bg: 'gray.50' }} onClick={getRowClickHandler(props)}>
      <Td>{exam.date ? getDateString(exam.date, 'short', true) : '-'}</Td>
      <Td>{d.studyType ?? '-'}</Td>
      <Td>{d.gaAtStudyDays != null ? formatGa(d.gaAtStudyDays) : '-'}</Td>
      <Td>{d.indication ? d.indication.slice(0, 40) : '-'}</Td>
      <Td>{initials}</Td>
      <EncounterStatusCell status={exam.status} />
      <SelectTd isSelecting={isSelecting} isChecked={isChecked} onToggle={onToggle} />
    </Tr>
  )
}

const PreConceptionRow: React.FC<EncounterRowProps<EncounterType.PRE_CONCEPTION>> = props => {
  const { encounter: exam, isSelecting, isChecked, onToggle } = props
  const initials = useInitials(exam)
  return (
    <Tr cursor="pointer" h="40px" _hover={{ bg: 'gray.50' }} onClick={getRowClickHandler(props)}>
      <Td>{exam.date ? getDateString(exam.date, 'short', true) : '-'}</Td>
      <Td>{initials}</Td>
      <EncounterStatusCell status={exam.status} />
      <SelectTd isSelecting={isSelecting} isChecked={isChecked} onToggle={onToggle} />
    </Tr>
  )
}

// export const encounterRowRecord: Record<
//   EncounterType,
//   { columns: string[]; Row: React.FC<EncounterRowProps> }
// > = {
export const encounterRowRecord: {
  [T in EncounterType]: { columns: string[]; Row: React.FC<EncounterRowProps<T>> }
} = {
  [EncounterType.PHYSICAL]: {
    columns: ['Date', 'Height', 'Weight', 'BMI', 'BP', 'Pulse', 'Temp', 'Initials'],
    Row: PhysicalExamRow,
  },
  [EncounterType.ENCOUNTER]: {
    columns: ['Date', 'Weight', 'BP', 'Chief Complaint', 'Physical', 'Pelvic', 'Initials'],
    Row: EncounterExamRow,
  },
  [EncounterType.WELL_WOMAN]: {
    columns: ['Date', 'Weight', 'BP', 'Chief Complaint', 'Physical', 'Education', 'Initials'],
    Row: WellWomanExamRow,
  },
  [EncounterType.PRE_CONCEPTION]: {
    columns: ['Date', 'Initials', 'Status'],
    Row: PreConceptionRow,
  },
  [EncounterType.ANTEPARTUM]: {
    columns: [
      'Date',
      'GA',
      'Wt.',
      'BP',
      'Pulse',
      'Temp',
      'Fundal Ht.',
      'FHT',
      'Position',
      'Initials',
      'Status',
    ],
    Row: AntepartumEncounterRow,
  },
  [EncounterType.POSTPARTUM]: {
    columns: ['Date', 'BP', 'Pulse', 'Temp', 'Lochia', 'Fundus', 'Perineum', 'Initials', 'Status'],
    Row: PostpartumEncounterRow,
  },
  [EncounterType.BIRTHING_CENTER_ADMISSION]: {
    columns: ['Date', 'Time', 'Admission Type', 'Membranes', 'Fluid', 'Birth Plan', 'Initials'],
    Row: BirthingCenterAdmissionRow,
  },
  [EncounterType.ULTRASOUND]: {
    columns: ['Date', 'Study Type', 'GA', 'Indication', 'Initials', 'Status'],
    Row: UltrasoundRow,
  },
}
